import { useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { defineMessages, useIntl } from 'react-intl'
import { toast } from 'react-toastify'

import { CONTACT_CRM_PANEL, CONTACT_TICKETS, CONTACT_CALLS } from '../../../gql/queries/chatQueries'
import { useTelephony } from '../../../hooks/useTelephony'
import { ModalPreview } from '../../contacts/components/ModalPreview'
import { ModalAddEditContact } from '../../contacts/components/ModalAddEditContact'
import { ModalAddPolice } from '../../policies/components/ModalAddPolice'
import { ModalRequestSignature } from '../../signatureForms/components/ModalRequestSignature'
import { ContactTicketModal } from './ContactTicketModal'
import { resolveConversationChannel } from '../helpers/channelBadgeHelper'
import { useGetUser } from '../../../store/userStore'
import { hasPermission } from '../../../helpers'
import { ALL_PERMISSIONS } from '../../../helpers/consts'
import type { Conversation } from '../hooks/useChatGraphQL'
import { ARROW_IN, ARROW_OUT, DOT_SEP, EM_DASH } from '../chatMessages'

const M = defineMessages({
  call: { id: 'inbox.crm.call', defaultMessage: 'Call' },
  newPolicy: { id: 'inbox.crm.newPolicy', defaultMessage: 'New policy' },
  newTicket: { id: 'inbox.crm.newTicket', defaultMessage: 'New ticket' },
  editContact: { id: 'inbox.crm.editContact', defaultMessage: 'Edit contact' },
  createContact: { id: 'inbox.crm.createContact', defaultMessage: 'Create contact' },
  viewProfile: { id: 'inbox.crm.viewProfile', defaultMessage: 'View profile' },
  requestSignature: { id: 'inbox.crm.requestSignature', defaultMessage: 'Request Signature' },
  createFirst: {
    id: 'inbox.crm.createFirst',
    defaultMessage: 'Create the contact first to request a signature.',
  },
  assigned: { id: 'inbox.crm.assigned', defaultMessage: 'Assigned' },
  unassigned: { id: 'inbox.crm.unassigned', defaultMessage: 'Unassigned' },
  pipeline: { id: 'inbox.crm.pipeline', defaultMessage: 'Pipeline' },
  noLead: { id: 'inbox.crm.noLead', defaultMessage: 'No lead' },
  policies: { id: 'inbox.crm.policies', defaultMessage: 'Policies' },
  noPolicies: { id: 'inbox.crm.noPolicies', defaultMessage: 'No policies' },
  tickets: { id: 'inbox.crm.tickets', defaultMessage: 'Open tickets' },
  noTickets: { id: 'inbox.crm.noTickets', defaultMessage: 'No open tickets' },
  recentCalls: { id: 'inbox.crm.recentCalls', defaultMessage: 'Recent calls' },
  noCalls: { id: 'inbox.crm.noCalls', defaultMessage: 'No recent calls' },
  missed: { id: 'inbox.crm.missed', defaultMessage: 'Missed' },
})

interface PolicyLite {
  id: string
  policyName?: string
  status?: string
  effectiveDate?: string
  type?: string
}

const initials = (name?: string | null) =>
  name
    ? name.trim().split(/\s+/).filter(Boolean).map((w) => w[0]).filter((_, i, a) => i === 0 || i === a.length - 1).join('').toUpperCase()
    : '#'

const policyStatusClass = (s?: string) => {
  const v = (s || '').toUpperCase()
  if (['ACTIVE', 'ACTIVA', 'COMPLETED', 'APPROVED', 'ENFORCE'].some((k) => v.includes(k))) return 'badge-light-success'
  if (['CANCEL', 'DECLIN', 'EXPIRED', 'TERM'].some((k) => v.includes(k))) return 'badge-light-danger'
  return 'badge-light-warning'
}
const fmtCallDate = (iso?: string) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// Collapsible panel section: the title row toggles the body open/closed.
const CrmSection: React.FC<{
  title: React.ReactNode
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}> = ({ title, open, onToggle, children }) => (
  <div className='crm-cp-sec'>
    <button type='button' className='crm-cp-toggle' onClick={onToggle} aria-expanded={open}>
      <span className='crm-cp-title mb-0'>{title}</span>
      <i className={`bi bi-chevron-${open ? 'down' : 'right'} crm-cp-chev`} />
    </button>
    {open && <div className='crm-cp-sec-body'>{children}</div>}
  </div>
)

export const ContactCrmPanel: React.FC<{ conversation: Conversation }> = ({ conversation }) => {
  const intl = useIntl()
  const telephony = useTelephony()
  const contactId = conversation.id

  const permissions = useGetUser((s: any) => s.user?.permissions) ?? []
  const canRequestSignature = hasPermission(permissions, ALL_PERMISSIONS.VIEW_SIGNATURE_FORMS)

  // In-place modals so the agent never leaves the Communications module: the full
  // client profile, create/edit contact, new policy and new ticket all overlay the
  // inbox and drop the agent right back into the chat on close.
  const [showProfile, setShowProfile] = useState(false)
  const [showContactForm, setShowContactForm] = useState(false)
  const [showPolicyForm, setShowPolicyForm] = useState(false)
  const [showTicketForm, setShowTicketForm] = useState(false)
  const [showSignature, setShowSignature] = useState(false)

  // Each panel section is collapsible; all start collapsed so the agent sees the
  // section titles + counts at a glance and expands only what they need.
  const [openSec, setOpenSec] = useState<Record<string, boolean>>({
    assigned: false,
    pipeline: false,
    policies: false,
    tickets: false,
    calls: false,
  })
  const toggleSec = (k: string) => setOpenSec((o) => ({ ...o, [k]: !o[k] }))

  const { data, refetch } = useQuery(CONTACT_CRM_PANEL, {
    variables: { id: String(contactId) },
    skip: !contactId,
    fetchPolicy: 'cache-and-network',
  })
  const { data: ticketData, refetch: refetchTickets } = useQuery(CONTACT_TICKETS, {
    variables: { contactId: String(contactId) },
    skip: !contactId,
    fetchPolicy: 'cache-and-network',
  })

  // Recent calls for THIS contact (any agent) from CommunicationLog — real last
  // calls with date + who contacted/attended + direction. No recording, no duration.
  const { data: callsData } = useQuery(CONTACT_CALLS, {
    variables: { contactId: String(contactId) },
    skip: !contactId,
    fetchPolicy: 'cache-and-network',
  })
  const calls = (callsData?.allCommunications?.edges || []).map((e: any) => e.node)

  const contact = data?.contactById
  const agent = contact?.user
  const stage = contact?.lead?.stagePipeline
  // Nombre del agente y sufijo de pipeline: son datos, no texto traducible.
  const agentFullName = agent ? `${agent.firstName} ${agent.lastName}` : ''
  const stagePipelineSuffix = stage?.pipeline?.name ? `${DOT_SEP}${stage.pipeline.name}` : ''

  const policies: PolicyLite[] = useMemo(() => {
    const out: PolicyLite[] = []
    const seen = new Set<string>()
    const push = (p?: PolicyLite) => {
      if (p && p.id && !seen.has(p.id)) {
        seen.add(p.id)
        out.push(p)
      }
    }
    push(contact?.memberPolicy?.policy)
    contact?.members?.edges?.forEach((e: any) => push(e?.node?.policy))
    return out
  }, [contact])

  const tickets = ticketData?.tickets?.items || []
  const tags = conversation.tags || []
  const email = contact?.email

  // A conversation always maps to a Contact row, but many are phone-only stubs.
  // Treat it as a "real" contact when it has an email or a name that isn't just the
  // phone. Compare the last 10 digits so a leading country code (e.g. +1) doesn't
  // make a phone-as-name look like a real name.
  const last10 = (s?: string | null) => (s || '').replace(/\D/g, '').slice(-10)
  const nameIsPhone =
    !!last10(conversation.full_name) && last10(conversation.full_name) === last10(conversation.phone)
  const isRealContact = !!(
    email || (conversation.full_name && conversation.full_name.trim().length > 1 && !nameIsPhone)
  )
  // Clicking the name opens the full read-only profile for a real contact; a
  // phone-only stub has no profile yet, so its name opens the create form instead.
  const openName = () => (isRealContact ? setShowProfile(true) : setShowContactForm(true))
  // Same behavior as hovering a phone number with PhoneProviderActions: dial through
  // PolicyDial when the agent is logged in; RingCentral fallback; else prompt to connect.
  const handleCall = () => {
    const n = String(conversation.phone || '').replace(/[^0-9+]/g, '')
    if (!n) {
      toast.error('This contact has no phone number.')
      return
    }
    if (telephony.config.vicidial_enabled && telephony.vicidialSession?.connected) {
      telephony.prepareVicidialDial(n, contact?.lead?.id, true)
    } else if (telephony.isRingCentralConnected) {
      telephony.startCall({ phone: n, leadId: contact?.lead?.id, providerOverride: 'RINGCENTRAL' })
    } else {
      toast.error('Connect PolicyDial from Integrations before starting a call.')
    }
  }

  return (
    <>
    <div className='crm-context-panel'>
      {/* Header */}
      <div className='crm-cp-head'>
        <div className='symbol symbol-65px symbol-circle mb-2'>
          {conversation.avatar ? (
            <img src={conversation.avatar} alt={conversation.full_name} />
          ) : (
            <span className='symbol-label bg-light-primary text-primary fs-2 fw-bolder'>
              {initials(conversation.full_name)}
            </span>
          )}
        </div>
        <button type='button' className='crm-cp-name-link fw-bold fs-4 text-gray-900' onClick={openName} title={intl.formatMessage(isRealContact ? M.viewProfile : M.createContact)}>
          {conversation.full_name}
        </button>
        {conversation.phone && <div className='text-gray-600 fs-7'>{conversation.phone}</div>}
        {email && <div className='text-gray-600 fs-8 text-truncate'>{email}</div>}
        {tags.length > 0 && (
          <div className='d-flex flex-wrap gap-1 justify-content-center mt-2'>
            {tags.map((t) => (
              <span
                key={t.id}
                className='badge badge-sm'
                style={{ backgroundColor: t.color + '20', color: t.color, border: `1px solid ${t.color}` }}
              >
                {t.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div className='crm-cp-actions'>
        <button className='btn btn-sm btn-primary w-100 crm-cp-call' onClick={handleCall}>
          <i className='bi bi-telephone me-1' />
          {intl.formatMessage(M.call)}
        </button>
        <button className='btn btn-sm btn-light w-100' onClick={() => setShowPolicyForm(true)}>
          <i className='bi bi-shield-plus me-1' />
          {intl.formatMessage(M.newPolicy)}
        </button>
        <button className='btn btn-sm btn-light w-100' onClick={() => setShowTicketForm(true)}>
          <i className='bi bi-ticket-perforated me-1' />
          {intl.formatMessage(M.newTicket)}
        </button>
        <button className='btn btn-sm btn-light w-100' onClick={() => setShowContactForm(true)}>
          <i className={`bi ${isRealContact ? 'bi-pencil-square' : 'bi-person-plus'} me-1`} />
          {intl.formatMessage(isRealContact ? M.editContact : M.createContact)}
        </button>
        {canRequestSignature && (
          <button
            className='btn btn-sm btn-light w-100'
            onClick={() => {
              // A signature can only be requested against a real contact; a phone-only
              // stub has no record yet, so send the agent to create it first.
              if (!isRealContact) {
                toast.info(intl.formatMessage(M.createFirst))
                setShowContactForm(true)
                return
              }
              setShowSignature(true)
            }}
          >
            <i className='bi bi-send me-1' />
            {intl.formatMessage(M.requestSignature)}
          </button>
        )}
      </div>

      {/* Assigned */}
      <CrmSection open={openSec.assigned} onToggle={() => toggleSec('assigned')} title={intl.formatMessage(M.assigned)}>
        <div className='d-flex align-items-center gap-2'>
          <div className='symbol symbol-30px symbol-circle'>
            <span className='symbol-label bg-light-info text-info fs-8 fw-bold'>
              {initials(agentFullName || conversation.assigned_to_name)}
            </span>
          </div>
          <div className='fs-7 fw-semibold text-gray-800'>
            {agentFullName || conversation.assigned_to_name || intl.formatMessage(M.unassigned)}
          </div>
        </div>
      </CrmSection>

      {/* Pipeline */}
      <CrmSection open={openSec.pipeline} onToggle={() => toggleSec('pipeline')} title={intl.formatMessage(M.pipeline)}>
        {stage ? (
          <span className='badge badge-light-primary fs-7'>
            {stage.name}
            {stagePipelineSuffix}
          </span>
        ) : (
          <div className='text-gray-500 fs-8'>{intl.formatMessage(M.noLead)}</div>
        )}
      </CrmSection>

      {/* Policies */}
      <CrmSection
        open={openSec.policies}
        onToggle={() => toggleSec('policies')}
        title={<>{intl.formatMessage(M.policies)} <span className={`crm-cp-n${policies.length ? ' is-primary' : ''}`}>{policies.length}</span></>}
      >
        {policies.length ? (
          policies.map((p) => (
            <div key={p.id} className='crm-cp-row'>
              <div className='crm-cp-ic bg-light-primary text-primary'>
                <i className='bi bi-shield-check' />
              </div>
              <div className='min-w-0 flex-grow-1'>
                <div className='fs-7 fw-semibold text-gray-800 text-truncate'>{p.policyName || EM_DASH}</div>
                {p.effectiveDate && <div className='fs-9 text-gray-500'>{p.effectiveDate}</div>}
              </div>
              <span className={`badge badge-sm ${policyStatusClass(p.status)}`}>{p.status || EM_DASH}</span>
            </div>
          ))
        ) : (
          <div className='text-gray-500 fs-8'>{intl.formatMessage(M.noPolicies)}</div>
        )}
      </CrmSection>

      {/* Tickets */}
      <CrmSection
        open={openSec.tickets}
        onToggle={() => toggleSec('tickets')}
        title={<>{intl.formatMessage(M.tickets)} <span className={`crm-cp-n${tickets.length ? ' is-warning' : ''}`}>{tickets.length}</span></>}
      >
        {tickets.length ? (
          tickets.map((t: any) => (
            <div key={t.id} className='crm-cp-row'>
              <div className='crm-cp-ic bg-light-warning text-warning'>
                <i className='bi bi-ticket-perforated' />
              </div>
              <div className='min-w-0 flex-grow-1'>
                <div className='fs-7 fw-semibold text-gray-800 text-truncate'>{t.title}</div>
                {t.assignedGroup?.name && <div className='fs-9 text-gray-500'>{t.assignedGroup.name}</div>}
              </div>
              <span className='badge badge-sm badge-light-warning'>{t.status}</span>
            </div>
          ))
        ) : (
          <div className='text-gray-500 fs-8'>{intl.formatMessage(M.noTickets)}</div>
        )}
      </CrmSection>

      {/* Recent calls */}
      <CrmSection
        open={openSec.calls}
        onToggle={() => toggleSec('calls')}
        title={<>{intl.formatMessage(M.recentCalls)} <span className={`crm-cp-n${calls.length ? ' is-info' : ''}`}>{calls.length}</span></>}
      >
        {calls.length ? (
          calls.map((c: any, i: number) => {
            const missed = !c.user
            const who = c.user ? `${c.user.firstName || ''} ${c.user.lastName || ''}`.trim() : ''
            return (
              <div key={c.id || i} className='crm-cp-call-row'>
                <div className='d-flex align-items-center gap-2'>
                  <i
                    className={`bi ${c.direction === 'INBOUND' ? 'bi-telephone-inbound' : 'bi-telephone-outbound'} ${
                      missed ? 'text-danger' : 'text-success'
                    }`}
                  />
                  <div className='min-w-0 flex-grow-1'>
                    <div className='fs-8 text-gray-700'>{fmtCallDate(c.created)}</div>
                    <div className='fs-9 text-gray-500'>
                      {c.direction === 'INBOUND' ? ARROW_IN : ARROW_OUT}
                      {missed ? intl.formatMessage(M.missed) : who || EM_DASH}
                    </div>
                  </div>
                </div>
              </div>
            )
          })
        ) : (
          <div className='text-gray-500 fs-8'>{intl.formatMessage(M.noCalls)}</div>
        )}
      </CrmSection>
    </div>

      {/* Full client profile (read-only) — overlays the inbox, closes back to chat */}
      {showProfile && (
        <ModalPreview idContact={String(contactId)} onClose={() => setShowProfile(false)} />
      )}

      {/* Create (phone-only stub) or edit (real contact) — in place, no navigation */}
      {showContactForm && (
        <ModalAddEditContact
          keyboard={false}
          backdrop='static'
          isOpen={showContactForm}
          onClose={() => setShowContactForm(false)}
          refetch={() => refetch()}
          idContact={isRealContact ? String(contactId) : undefined}
          initialPhone={isRealContact ? undefined : conversation.phone || ''}
        />
      )}

      {/* New policy — prefilled with this contact, overlaying the inbox */}
      {showPolicyForm && (
        <ModalAddPolice
          keyboard={false}
          backdrop='static'
          isOpen={showPolicyForm}
          onClose={() => setShowPolicyForm(false)}
          refetch={() => refetch()}
          idContact={String(contactId)}
        />
      )}

      {/* New ticket — quick create linked to this contact, without leaving the chat */}
      {showTicketForm && (
        <ContactTicketModal
          show={showTicketForm}
          onClose={() => setShowTicketForm(false)}
          channel={resolveConversationChannel(conversation) || 'INTERNAL'}
          policies={policies}
          contactName={conversation.full_name}
          onCreated={() => refetchTickets()}
        />
      )}

      {/* Request signature — overlays the inbox, closes back to chat */}
      {showSignature && (
        <ModalRequestSignature
          keyboard={false}
          backdrop='static'
          isOpen={showSignature}
          sourceId={String(contactId)}
          sourceType='contact'
          onClose={() => setShowSignature(false)}
          refetch={() => refetch()}
          hasEmail={!!email}
          hasPhone={!!conversation.phone}
        />
      )}
    </>
  )
}
